import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import * as api from '../../lib/api'
import { useToast } from '../../contexts/ToastContext'
import * as XLSX from 'xlsx'
import { useTour } from '../../contexts/TourContext'
import ChessGrid from './ChessGrid'
import { getPermission } from '../../lib/permissionDefaults'

// Импорт новых подкомпонентов
import OperationsFeed from './components/OperationsFeed'
import SupplyRequests from './components/SupplyRequests'
import OperationFormModal from './components/OperationFormModal'
import ExportImportModals from './components/ExportImportModals'
import ShiftTimer from '../../components/ShiftTimer/ShiftTimer'

import './OperationsPage.css'

const todayIso = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
const yesterdayIso = () => {
  const d = new Date(); d.setDate(d.getDate() - 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
const monthStartIso = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}
const monthEndIso = () => {
  const d = new Date()
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
}
const quarterStartIso = () => {
  const d = new Date()
  const q = Math.floor(d.getMonth() / 3)
  return `${d.getFullYear()}-${String(q * 3 + 1).padStart(2, '0')}-01`
}
const quarterEndIso = () => {
  const d = new Date()
  const q = Math.floor(d.getMonth() / 3)
  const lastMonth = q * 3 + 2
  const lastDay = new Date(d.getFullYear(), lastMonth + 1, 0).getDate()
  return `${d.getFullYear()}-${String(lastMonth + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
}

const today = todayIso

export default function OperationsPage() {
  const { profile, isAdmin, activePvz, selectedPvzIds, userPvzs, adminPermissions: userPermissions } = useAuth()
  const isOwner = profile?.role === 'owner'
  const toast = useToast()
  const tour = useTour()

  // Имитация интерфейса сотрудника для владельца во время прохождения тура по операциям
  const isDemoStaffView = tour.isActive && 
    (tour.steps[tour.currentStep]?.tab === 'operations') && 
    profile?.role === 'owner'

  // Доступ к админ-панелям (скрывается в демо-режиме сотрудника)
  const displayAsAdmin = (isAdmin || userPermissions?.canManageOperations) && !isDemoStaffView

  const getPvzFullName = useCallback((pvzId) => {
    const pvz = userPvzs?.find(p => p.id === pvzId)
    return pvz ? pvz.name : ''
  }, [userPvzs])

  const getPvzShortName = useCallback((pvzId) => {
    const pvz = userPvzs?.find(p => p.id === pvzId)
    if (!pvz) return ''
    const cleanName = pvz.name.replace(/пвз|wildberries|wb|пункт|выдачи/gi, '').trim()
    const numberMatch = pvz.name.match(/\d+/)
    const parts = cleanName.split(/[\s,\-]+/).filter(Boolean)
    if (parts.length === 0) return pvz.name.slice(0, 4)
    if (numberMatch) {
      const num = numberMatch[0]
      const firstChar = parts[0] ? parts[0][0].toUpperCase() : ''
      return `${firstChar}${num}`
    }
    if (parts.length >= 2) {
      return parts.map(p => p[0].toUpperCase()).join('').slice(0, 3)
    }
    return parts[0].slice(0, 4)
  }, [userPvzs])

  const PVZ_COLORS = ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#06b6d4', '#f43f5e']
  const getPvzColor = useCallback((pvzId) => {
    const idx = userPvzs?.findIndex(p => p.id === pvzId) ?? 0
    return PVZ_COLORS[Math.max(0, idx) % PVZ_COLORS.length]
  }, [userPvzs])

  const canAddForOthers = isOwner || getPermission(userPermissions, 'canAddOperationsForOthers', profile?.role)

  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [quickAddContext, setQuickAddContext] = useState(null)
  const [quickAddSelectedTypes, setQuickAddSelectedTypes] = useState([])
  const [employees, setEmployees] = useState([])
  const [customRules, setCustomRules] = useState([])
  const [salaryConfig, setSalaryConfig] = useState(null)

  // Получение drilldown параметров из sessionStorage
  const getDrilldown = () => {
    try {
      const stored = sessionStorage.getItem('ops_drilldown')
      if (stored) {
        sessionStorage.removeItem('ops_drilldown')
        return JSON.parse(stored)
      }
    } catch(e) {}
    return null
  }
  const drilldown = useMemo(getDrilldown, [])

  // Фильтры
  const [filterStartDate, setFilterStartDate] = useState(drilldown?.startDate || todayIso())
  const [filterEndDate, setFilterEndDate] = useState(drilldown?.endDate || todayIso())
  const [filterEmployee, setFilterEmployee] = useState(drilldown?.employeeId || '')
  const [filterType, setFilterType] = useState(drilldown?.type || 'all')
  const [filterSearch, setFilterSearch] = useState('')

  // Стейты модалок операций
  const [showModal, setShowModal] = useState(false)
  const [activeActionType, setActiveActionType] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [confirmData, setConfirmData] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showAddMenu, setShowAddMenu] = useState(false)

  // Стейты экспорта/импорта
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [exportStartDate, setExportStartDate] = useState(todayIso())
  const [exportEndDate, setExportEndDate] = useState(todayIso())
  const [exporting, setExporting] = useState(false)
  const [exportFormat, setExportFormat] = useState('xls')
  const [showImport, setShowImport] = useState(false)
  const [showImportHistory, setShowImportHistory] = useState(false)
  const [importHistory, setImportHistory] = useState([])
  const [deletingBatch, setDeletingBatch] = useState(null)
  const [exportColumns, setExportColumns] = useState({
    date: true,
    time: true,
    employee: true,
    type: true,
    value: true,
    late: true,
    box: true,
    returns_time: true,
    comment: true
  })

  // Режим отображения: лента (feed) или шахматка (chess)
  const [viewMode, setViewMode] = useState(() => {
    if (drilldown?.viewMode) {
      try { localStorage.setItem('ops_view_mode', drilldown.viewMode) } catch(e) {}
      return drilldown.viewMode
    }
    try { return localStorage.getItem('ops_view_mode') || 'feed' } catch(e) { return 'feed' }
  })
  const handleSetViewMode = (mode) => {
    setViewMode(mode)
    try { localStorage.setItem('ops_view_mode', mode) } catch(e) {}
  }

  // Режим работы на смене для Владельца/Администратора
  const [showShiftTimerForAdmin, setShowShiftTimerForAdmin] = useState(() => {
    try { return localStorage.getItem('ops_admin_shift_mode') === 'true' } catch(e) { return false }
  })
  const handleToggleAdminShiftMode = () => {
    setShowShiftTimerForAdmin(prev => {
      const next = !prev
      try { localStorage.setItem('ops_admin_shift_mode', next ? 'true' : 'false') } catch(e) {}
      return next
    })
  }

  // Переключение вкладок: 'operations' (Все операции) или 'supplies' (Заявки на хознужды)
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem('default_operations_tab')
      if (saved) {
        localStorage.removeItem('default_operations_tab') // очищаем флаг сразу после считывания
        return saved
      }
    } catch(e) {}
    return 'operations'
  })

  // Слушатель смены вкладки при открытой странице операций (из deep-link уведомлений)
  useEffect(() => {
    const handleOpsTab = (e) => {
      const tab = e?.detail?.tab || localStorage.getItem('default_operations_tab')
      if (tab) {
        setActiveTab(tab)
        try { localStorage.removeItem('default_operations_tab') } catch (err) {}
      }
    }
    window.addEventListener('operations-tab-navigate', handleOpsTab)
    return () => window.removeEventListener('operations-tab-navigate', handleOpsTab)
  }, [])

  // Новые/активные заявки на хознужды для бейджа на вкладке
  const [unreadSuppliesCount, setUnreadSuppliesCount] = useState(0)

  // История изменений операций
  const [historyRecords, setHistoryRecords] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)

  const canSeeHistory = isOwner || isAdmin || profile?.role === 'admin_employee'

  const loadHistory = useCallback(async () => {
    if (!profile?.id) return
    const isPrivileged = ['owner', 'admin', 'admin_employee'].includes(profile.role)
    if (!isPrivileged) return

    try {
      setLoadingHistory(true)
      const data = await api.getWorkLogsAuditLogs({ limit: 80 })
      setHistoryRecords(data || [])
    } catch (err) {
      console.error('Failed to load history:', err)
      toast.error('Не удалось загрузить историю изменений')
    } finally {
      setLoadingHistory(false)
    }
  }, [profile?.id, profile?.role])

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory()
    }
  }, [activeTab, loadHistory])

  useEffect(() => {
    if (!profile?.id || !activePvz?.id) return
    const isPrivileged = ['owner', 'admin', 'admin_employee'].includes(profile.role)
    if (!isPrivileged) return

    const loadBadge = async () => {
      try {
        let count = 0
        if (profile.role === 'owner') {
          count = await api.getOwnerActiveSuppliesCount(profile.org_id, activePvz.id)
        } else {
          count = await api.getPendingSupplyRequestsCount(profile.org_id, activePvz.id)
        }
        setUnreadSuppliesCount(count)
      } catch (e) {
        console.warn('Error loading supply requests tab badge:', e)
      }
    }
    loadBadge()

    const handleChanged = () => loadBadge()
    window.addEventListener('supply-requests-changed', handleChanged)
    return () => {
      window.removeEventListener('supply-requests-changed', handleChanged)
    }
  }, [profile?.id, profile?.role, profile?.org_id, activePvz?.id])

  const [formData, setFormData] = useState({
    date: today(),
    count: '',
    boxNumber: '',
    comment: '',
    customRuleId: '',
    startTime: '',
    endTime: '',
    employeeId: profile?.id || ''
  })

  // Конфиг базовых типов
  const baseActions = [
    { id: 'acceptance', title: 'Приёмка', icon: <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg></>, color: 'var(--wb-primary)' },
    { id: 'issued', title: 'Выдача', icon: <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg></>, color: 'var(--success)' },
    { id: 'returns', title: 'Возврат', icon: <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg></>, color: 'var(--info)' }
  ]

  const [scheduleData, setScheduleData] = useState([])

  // Начальная загрузка
  useEffect(() => {
    console.log('[OperationsPage] Check conditions:', { isAdmin, activePvzId: activePvz?.id, orgId: profile?.org_id, profile })
    if (isAdmin && activePvz?.id && profile?.org_id) {
      console.log('[OperationsPage] Calling getAllEmployeesList')
      api.getAllEmployeesList(activePvz.id, profile.org_id).then(res => {
        console.log('[OperationsPage] setEmployees called with:', res)
        setEmployees(res)
      }).catch(console.error)
    }
    api.getSalaryConfig().then(cfg => {
      setSalaryConfig(cfg)
      setCustomRules((cfg.customRules || []).filter(r => r.type !== 'fixed'))
    }).catch(console.error)
  }, [isAdmin, activePvz?.id, profile?.org_id])

  // Загрузка расписания + операций для маркеров на календаре
  const [opsLogMap, setOpsLogMap] = useState({})
  const [markerVersion, setMarkerVersion] = useState(0)
  
  const refreshMarkers = useCallback(() => setMarkerVersion(v => v + 1), [])
  
  useEffect(() => {
    if (!activePvz?.id || !profile?.id) return
    const loadScheduleAndOps = async () => {
      try {
        const now = new Date()
        const curr = await api.getSchedule(now.getFullYear(), now.getMonth() + 1)
        const prevM = now.getMonth() === 0 ? 12 : now.getMonth()
        const prevY = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
        const prev = await api.getSchedule(prevY, prevM)
        const nextM = now.getMonth() + 2 > 12 ? 1 : now.getMonth() + 2
        const nextY = now.getMonth() + 2 > 12 ? now.getFullYear() + 1 : now.getFullYear()
        const next = await api.getSchedule(nextY, nextM)
        let allSchedule = [...(prev || []), ...(curr || []), ...(next || [])]
        const isPrivileged = isOwner || (profile?.role === 'admin' || profile?.role === 'admin_employee')
        if (!isPrivileged && !canAddForOthers) {
          allSchedule = allSchedule.filter(s => s.employee_id === profile.id)
        }
        setScheduleData(allSchedule)

        const fmtD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        const startD = new Date(prevY, prevM - 1, 1)
        const endD = new Date(nextY, nextM - 1 + 1, 0)
        const logType = (isPrivileged || canAddForOthers) ? 'all' : profile.id
        const allLogs = await api.getWorkLogs(logType, { startDate: fmtD(startD), endDate: fmtD(endD), pvzId: activePvz.id })
        
        const logMap = {}
        ;(allLogs || []).forEach(l => {
          const key = `${l.employee_id}_${l.work_date}`
          if (!logMap[key]) logMap[key] = { total: 0, types: new Set(), dismissedTypes: [] }
          
          if (l.comment === '[DISMISSED]') {
            if (l.type) logMap[key].dismissedTypes.push(l.type)
          } else {
            logMap[key].total += (l.count || 0) + (l.count_late || 0)
            if (l.type) logMap[key].types.add(l.type)
          }
        })
        setOpsLogMap(logMap)
      } catch(e) {
        console.error('Failed to load schedule/ops for calendar markers', e)
      }
    }
    loadScheduleAndOps()
  }, [activePvz?.id, profile?.id, canAddForOthers, markerVersion])

  // Маркеры смен для календаря
  const EMP_COLORS = ['#14B8A6', '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899']
  const shiftMarkers = useMemo(() => {
    if (!scheduleData.length) return null
    const empList = employees.length ? employees : (profile ? [{ id: profile.id, name: profile.full_name || profile.name || 'Я', is_legacy: false }] : [])
    if (!empList.length) return null
    const markers = {}
    const todayStr = todayIso()
    const empColorMap = {}
    empList.forEach((emp, idx) => {
      empColorMap[emp.id] = EMP_COLORS[idx % EMP_COLORS.length]
    })
    
    scheduleData.forEach(s => {
      if (!s.work_date) return
      const wDate = String(s.work_date).split('T')[0]
      const emp = empList.find(e => e.id === s.employee_id)
      if (!emp) return
      if (!markers[wDate]) markers[wDate] = []
      if (!markers[wDate].find(m => m.id === emp.id)) {
        const key = `${emp.id}_${wDate}`
        const logEntry = opsLogMap[key]
        const opsTypes = logEntry ? logEntry.types : new Set()
        const isFuture = wDate >= todayStr
        
        const isHelper = emp.role === 'helper' || s.shift_role === 'helper'
        const isHourlyHelper = isHelper && (salaryConfig?.helperHourlyRate > 0)
        const requiredTypes = isHourlyHelper ? [] : ['acceptance', 'issued', 'returns']
        
        const dismissedTypes = logEntry ? logEntry.dismissedTypes || [] : []
        const missingTypes = isFuture ? [] : requiredTypes.filter(t => !opsTypes.has(t) && !dismissedTypes.includes(t))
        const filled = isFuture ? null : missingTypes.length === 0
        markers[s.work_date].push({
          id: emp.id,
          name: emp.full_name || emp.name || 'Сотрудник',
          color: empColorMap[emp.id] || '#999',
          filled,
          missingTypes,
          dismissedTypes
        })
      }
    })
    return markers
  }, [scheduleData, employees, opsLogMap, profile, salaryConfig])

  // Загрузка логов
  const loadRecords = useCallback(async () => {
    if (!profile?.id || !activePvz?.id) return
    try {
      setLoading(true)
      const data = await api.getWorkLogs('all', {
        startDate: filterStartDate || undefined,
        endDate: filterEndDate || undefined,
        employeeId: (!isAdmin && !userPermissions?.canManageOperations) ? profile.id : (filterEmployee || undefined),
      })
      
      let allRecords = data;
      if (isAdmin || userPermissions?.canManageOperations) {
        try {
          const expenses = await api.getExpenses(activePvz.id, filterStartDate, filterEndDate);
          let varExpenses = expenses.filter(e => e.type === 'variable').map(e => {
            let quantity = 1;
            let cleanComment = e.comment || '';
            let authorName = 'ПВЗ (Аналитика)';
            
            const matchQty = cleanComment.match(/^Кол-во:\s*(\d+)\s*шт\.?\s*/);
            if (matchQty) {
              quantity = parseInt(matchQty[1], 10);
              cleanComment = cleanComment.replace(matchQty[0], '');
            }
            
            const matchAuthor = cleanComment.match(/\s*\(Автор:\s*(.*?)\)\s*/);
            if (matchAuthor) {
              authorName = matchAuthor[1].trim();
              cleanComment = cleanComment.replace(matchAuthor[0], '');
            }

            const emp = (employees || []).find(empObj => 
              (empObj.full_name && empObj.full_name.trim().toLowerCase() === authorName.toLowerCase()) || 
              (empObj.name && empObj.name.trim().toLowerCase() === authorName.toLowerCase())
            );
            
            return {
              id: 'exp_' + e.id,
              real_id: e.id,
              type: 'expense_var',
              work_date: e.date,
              created_at: e.created_at || new Date(e.date).toISOString(),
              count: e.amount,
              count_late: quantity,
              box_number: e.category,
              comment: cleanComment.trim(),
              employee_id: emp ? emp.id : null,
              employee_name: authorName
            }
          }).filter(e => e.employee_name !== 'ПВЗ (Аналитика)');
          
          if (filterEmployee) {
            varExpenses = varExpenses.filter(e => e.employee_id === filterEmployee);
          }
          
          allRecords = [...data, ...varExpenses].sort((a,b) => new Date(b.work_date || 0) - new Date(a.work_date || 0));
        } catch(e) {}
      }
      setRecords(allRecords)
    } catch (err) {
      console.error(err)
      toast.error('Ошибка загрузки операций')
    } finally {
      setLoading(false)
    }
  }, [profile?.id, activePvz?.id, filterStartDate, filterEndDate, filterEmployee, isAdmin, employees])

  useEffect(() => {
    loadRecords()
  }, [loadRecords])

  const [pendingChangeRequests, setPendingChangeRequests] = useState([])

  const loadPendingChangeRequests = useCallback(async () => {
    const isPrivileged = ['owner', 'admin', 'admin_employee'].includes(profile?.role)
    if (!isPrivileged || !activePvz?.id) return
    try {
      const pvzFilter = selectedPvzIds?.length > 0 ? selectedPvzIds.join(',') : activePvz.id
      const data = await api.getPendingWorkLogChangeRequests(pvzFilter)
      setPendingChangeRequests(data || [])
    } catch (err) {
      console.warn('Failed to load pending change requests:', err)
    }
  }, [profile?.role, activePvz?.id, selectedPvzIds])

  const handleResolveChangeRequest = useCallback(async (requestId, isApproved, rejectionReason = '') => {
    try {
      const result = await api.resolveWorkLogChangeRequest(requestId, isApproved, rejectionReason)
      if (result && result.code === 'DATA_CONFLICT') {
        return result
      }
      toast.success(isApproved ? 'Запрос успешно одобрен' : 'Запрос отклонён')
      loadPendingChangeRequests()
      loadRecords()
      if (canSeeHistory) loadHistory()
      return result
    } catch (err) {
      if (err?.code === 'DATA_CONFLICT' || err?.status === 409) {
        return err
      }
      toast.error(err.message || 'Ошибка обработки запроса')
      throw err
    }
  }, [loadPendingChangeRequests, loadRecords, canSeeHistory, loadHistory, toast])

  useEffect(() => {
    loadPendingChangeRequests()
  }, [loadPendingChangeRequests])

  const handleOpenAction = (actionType, customRuleId = null) => {
    setActiveActionType(actionType)
    setEditingId(null)
    setFormData({
      date: quickAddContext?.date || (isAdmin ? (filterStartDate === filterEndDate && filterStartDate ? filterStartDate : today()) : today()),
      count: '0',
      countLate: '0',
      boxNumber: '',
      comment: '',
      customRuleId: customRuleId || '',
      startTime: '',
      endTime: '',
      employeeId: quickAddContext?.employeeId || profile?.id || '',
      pvzId: quickAddContext?.pvzId || (selectedPvzIds && selectedPvzIds.length > 0 ? selectedPvzIds[0] : activePvz?.id || '')
    })
    setShowModal(true)
  }

  // Быстрый ввод
  const handleQuickAdd = useCallback((date, employeeId, pvzId) => {
    const emp = employees.find(e => e.id === employeeId)
    const empName = emp ? (emp.full_name || emp.name) : 'Сотрудник'
    const dateFormatted = new Date(date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'short' })
    const marker = shiftMarkers?.[date]?.find(m => m.id === employeeId)
    const missingTypes = marker?.missingTypes || []
    const typeLabels = { acceptance: 'Приёмка', issued: 'Выдача', returns: 'Возврат' }
    const missingLabels = missingTypes.map(t => typeLabels[t] || t)
    setQuickAddContext({ date, employeeId, empName, dateFormatted, missingLabels })
    setActiveActionType('acceptance')
    setEditingId(null)
    setFormData({
      date: date,
      count: '0',
      countLate: '0',
      boxNumber: '',
      comment: '',
      customRuleId: '',
      startTime: '',
      endTime: '',
      employeeId: employeeId || profile?.id || '',
      pvzId: pvzId || ''
    })
    setShowAddMenu(true)
  }, [profile?.id, employees, shiftMarkers])

  // Автооткрытие быстрого ввода при переходе по drilldown (из Дашборда)
  useEffect(() => {
    if (drilldown?.autoOpenQuickAdd && drilldown?.startDate && drilldown?.employeeId) {
      const isReady = isAdmin ? employees.length > 0 : true
      if (isReady) {
        const timer = setTimeout(() => {
          handleQuickAdd(drilldown.startDate, drilldown.employeeId, activePvz?.id || '')
        }, 150)
        return () => clearTimeout(timer)
      }
    }
  }, [drilldown, employees, isAdmin, activePvz?.id, handleQuickAdd])

  // Интеграция шагов обучения (авто-открытие модалки и переключение вида Шахматки)
  useEffect(() => {
    if (tour.isActive && tour.steps[tour.currentStep]) {
      const stepId = tour.steps[tour.currentStep].id

      // Авто-открытие формы операции
      if (stepId === 'operations-demo') {
        if (!showModal) {
          setActiveActionType('acceptance')
          setEditingId(null)
          setFormData({
            date: today(),
            count: '150',
            countLate: '10',
            boxNumber: '23',
            comment: 'Пример внесения операции (Обучение)',
            customRuleId: '',
            startTime: '',
            endTime: '',
            employeeId: profile?.id || '',
            pvzId: activePvz?.id || ''
          })
          setShowModal(true)
        }
      } else {
        // Закрываем модалку, если ушли с демонстрационного шага
        if (showModal && formData.comment === 'Пример внесения операции (Обучение)') {
          setShowModal(false)
        }
      }

      // Автоматический переключатель Лента / Шахматка
      if (stepId === 'operations-chess-overview' || stepId === 'operations-chess-expand' || stepId === 'operations-chess-cell') {
        if (viewMode !== 'chess') {
          setViewMode('chess')
        }
      } else if (stepId === 'operations-view-switcher' || stepId === 'operations-tabs' || stepId === 'operations-filters' || stepId === 'operations-add' || stepId === 'operations-demo') {
        if (viewMode !== 'feed') {
          setViewMode('feed')
        }
      }
    } else {
      // Если тур завершился/закрылся и модалка была открыта (например, через быстрый пропуск на шаге 4)
      if (showModal && formData.comment === 'Пример внесения операции (Обучение)') {
        setShowModal(false)
      }
    }
  }, [tour.isActive, tour.currentStep, showModal, viewMode, profile?.id, activePvz?.id])

  // Клик на ячейку шахматки
  const handleChessCellClick = useCallback((date, employeeId, typeStr, pvzId) => {
    let actionType = typeStr
    let customRuleId = null
    if (typeStr.startsWith('custom:')) {
      actionType = 'custom'
      customRuleId = typeStr.split(':')[1]
    }
    const emp = employees.find(e => e.id === employeeId)
    const empName = emp ? (emp.full_name || emp.name) : 'Сотрудник'
    const dateFormatted = new Date(date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'short' })

    setQuickAddContext({ date, employeeId, empName, dateFormatted, missingLabels: [] })
    setActiveActionType(actionType)
    setEditingId(null)
    setFormData({
      date: date,
      count: '0',
      countLate: '0',
      boxNumber: '',
      comment: '',
      customRuleId: customRuleId || '',
      startTime: '',
      endTime: '',
      employeeId: employeeId || profile?.id || '',
      pvzId: pvzId || ''
    })
    setShowModal(true)
  }, [profile?.id, employees])

  // Пропустить операцию
  const handleDismissShift = useCallback(async (date, employeeId, opType) => {
    try {
      await api.createWorkLog(opType, {
        employee_id: employeeId,
        work_date: date,
        comment: '[DISMISSED]',
        count: 0
      })
      refreshMarkers()
      loadRecords()
      window.dispatchEvent(new Event('operationsUpdated'))
    } catch(e) {
      console.error('Failed to dismiss shift', e)
      toast.error('Ошибка при сохранении пропуска')
    }
  }, [refreshMarkers, loadRecords])

  // Восстановить пропущенную операцию
  const handleRestoreShift = useCallback(async (date, employeeId, opType) => {
    try {
      const logs = await api.getWorkLogs(employeeId, { startDate: date, endDate: date })
      const logToDelete = logs.find(l => l.type === opType && l.comment === '[DISMISSED]')
      if (logToDelete) {
        await api.deleteWorkLog(logToDelete.id)
      }
      refreshMarkers()
      loadRecords()
      window.dispatchEvent(new Event('operationsUpdated'))
    } catch(e) {
      console.error('Failed to restore shift', e)
      toast.error('Ошибка при восстановлении смены')
    }
  }, [refreshMarkers, loadRecords])

  const handleEdit = (record) => {
    let type = record.type
    let customRuleId = ''
    if (type && type.startsWith('custom:')) {
      customRuleId = type.split(':')[1]
      type = 'custom'
    }

    setActiveActionType(type)
    setEditingId(record.id)
    setFormData({
      date: record.work_date || today(),
      count: record.count || '',
      countLate: record.count_late || '',
      boxNumber: record.box_number || '',
      comment: record.comment || '',
      customRuleId: customRuleId,
      startTime: record.start_time || '',
      endTime: record.end_time || '',
      employeeId: record.employee_id || profile?.id || '',
      authorName: record.type === 'expense_var' && record.employee_name !== 'ПВЗ (Аналитика)' ? record.employee_name : '',
      pvzId: record.pvz_id || ''
    })
    setShowModal(true)
  }

  const handlePreSubmit = (e) => {
    e.preventDefault()
    setConfirmData(true)
  }

  const handleSubmit = async () => {
    setConfirmData(false)
    setSaving(true)
    try {
      let finalType = activeActionType
      let dbRecord = {
        employee_id: (canAddForOthers && formData.employeeId) ? formData.employeeId : profile.id,
        work_date: formData.date,
        comment: formData.comment || null,
        pvz_id: formData.pvzId || null
      }

      if (activeActionType === 'custom') {
        finalType = `custom:${formData.customRuleId}`
        dbRecord.count = Number(formData.count) || 0
      } else if (activeActionType === 'returns') {
        dbRecord.box_number = formData.boxNumber
        dbRecord.start_time = formData.startTime || null
        dbRecord.end_time = formData.endTime || null
        dbRecord.count = Number(formData.count) || 0
      } else if (activeActionType === 'acceptance') {
        dbRecord.count = Number(formData.count) || 0
        dbRecord.count_late = Number(formData.countLate) || 0
      } else {
        dbRecord.count = Number(formData.count) || 0
      }

      if (editingId) {
        if (activeActionType === 'expense_var') {
          const realId = editingId.replace('exp_', '');
          let commentStr = formData.comment || '';
          const selectedEmp = employees.find(e => e.id === formData.employeeId);
          const authorName = selectedEmp ? (selectedEmp.full_name || selectedEmp.name) : (formData.authorName || profile?.full_name || 'Сотрудник');

          if (authorName) {
            commentStr = commentStr + ` (Автор: ${authorName})`;
          }
          if (formData.countLate) {
            commentStr = `Кол-во: ${formData.countLate} шт. ` + commentStr;
          }
          await api.updateExpense(realId, { amount: Number(formData.count) || 0, comment: commentStr.trim() || null, date: formData.date, category: formData.boxNumber || 'Хозтовары' });
        } else {
          try {
            await api.updateWorkLog(editingId, { ...dbRecord, type: finalType })
            toast.success('Запись успешно обновлена')
          } catch (err) {
            if (err.code === 'SHIFT_CLOSED_REQUIRE_REQUEST' || err.status === 403) {
              if (!formData.reason || !formData.reason.trim()) {
                toast.warning('Смена закрыта. Укажите причину для отправки запроса руководителю.')
                setConfirmData(false)
                return
              }
              try {
                await api.requestWorkLogChange(editingId, { ...dbRecord, type: finalType }, formData.reason)
                toast.success('Запрос отправлен на рассмотрение руководителю')
                loadPendingChangeRequests()
              } catch (reqErr) {
                if (reqErr.code === 'PENDING_EXISTS') {
                  toast.warning('Запрос на изменение этой записи уже ожидает рассмотрения')
                } else {
                  toast.error(reqErr.message || 'Ошибка создания запроса')
                }
              }
            } else {
              toast.error(err.message || 'Ошибка обновления записи')
            }
          }
        }
      } else {
        if (activeActionType === 'expense_var') {
          let commentStr = formData.comment || '';
          const selectedEmp = employees.find(e => e.id === formData.employeeId);
          const authorName = selectedEmp ? (selectedEmp.full_name || selectedEmp.name) : (profile?.full_name || 'Сотрудник');

          if (authorName) {
            commentStr = commentStr + ` (Автор: ${authorName})`;
          }
          if (formData.countLate) {
            commentStr = `Кол-во: ${formData.countLate} шт. ` + commentStr;
          }
          await api.addExpense({ amount: Number(formData.count) || 0, comment: commentStr.trim() || null, date: formData.date, category: formData.boxNumber || 'Хозтовары', type: 'variable', is_paid: true }, activePvz.id);
        } else {
          await api.createWorkLog(finalType, dbRecord)
        }
        toast.success('Запись успешно добавлена')
      }
      
      setShowModal(false)
      
      if (formData.employeeId && formData.date && activeActionType) {
        try {
          const logs = await api.getWorkLogs('all', { startDate: formData.date, endDate: formData.date, employeeId: formData.employeeId })
          const logToDelete = logs.find(l => l.type === activeActionType && l.comment === '[DISMISSED]')
          if (logToDelete) {
            await api.deleteWorkLog(logToDelete.id)
          }
        } catch(e) {
          console.warn('Failed to auto-clear db dismissed shift', e)
        }
      }
      
      loadRecords()
      refreshMarkers()
      if (canSeeHistory) loadHistory()
      window.dispatchEvent(new Event('operationsUpdated'))
      
      if (quickAddContext && !editingId) {
        setTimeout(() => {
          const marker = shiftMarkers?.[quickAddContext.date]?.find(m => m.id === quickAddContext.employeeId)
          const justFilled = activeActionType
          const remaining = (marker?.missingTypes || []).filter(t => t !== justFilled)
          if (remaining.length > 0) {
            const typeLabels = { acceptance: 'Приёмка', issued: 'Выдача', returns: 'Возврат' }
            setQuickAddContext(prev => prev ? { ...prev, missingLabels: remaining.map(t => typeLabels[t] || t) } : null)
            setShowAddMenu(true)
          } else {
            setQuickAddContext(null)
          }
        }, 500)
      }
    } catch (err) {
      console.error(err)
      toast.error('Ошибка сохранения: ' + (err.message || ''))
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    if (!itemToDelete) return
    try {
      const deletedRecord = displayedRecords.find(r => r.id === itemToDelete)
      
      if (typeof itemToDelete === 'string' && itemToDelete.startsWith('exp_')) {
        await api.deleteExpense(itemToDelete.replace('exp_', ''));
      } else {
        await api.deleteWorkLog(itemToDelete);
      }
      
      toast.success('Запись удалена')
      loadRecords()
      refreshMarkers()
      if (canSeeHistory) loadHistory()
      window.dispatchEvent(new Event('operationsUpdated'))
    } catch (err) {
      console.error(err)
      toast.error('Ошибка удаления')
    } finally {
      setItemToDelete(null)
    }
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      const data = await api.getWorkLogs('all', {
        startDate: exportStartDate,
        endDate: exportEndDate,
      })

      if (!data || data.length === 0) {
        toast.error('Нет данных для выгрузки за этот период')
        return
      }

      const grouped = {}
      data.forEach(r => {
        const key = `${r.work_date}_${r.employee_id}`
        if (!grouped[key]) {
          grouped[key] = {
            'Дата': r.work_date,
            'Сотрудник': r.employee_name || '',
            'Приёмка': 0,
            'Приёмка (опоздание)': 0,
            'Выдача': 0,
            'Возвраты': 0,
            'Коробки': [],
            'Прочее': 0,
            'Комментарии': []
          }
        }
        
        const g = grouped[key]
        if (r.type === 'acceptance') {
          g['Приёмка'] += (r.count || 0)
          g['Приёмка (опоздание)'] += (r.count_late || 0)
        } else if (r.type === 'issued' || r.type === 'issue') {
          g['Выдача'] += (r.count || 0)
        } else if (r.type === 'returns') {
          g['Возвраты'] += (r.count || 0)
          if (r.box_number) g['Коробки'].push(r.box_number)
        } else {
          g['Прочее'] += (r.count || 0)
        }
        
        if (r.comment) g['Комментарии'].push(r.comment)
      })

      const rows = Object.values(grouped).map(g => ({
        ...g,
        'Коробки': [...new Set(g['Коробки'])].join(', '),
        'Комментарии': [...new Set(g['Комментарии'])].join('; ')
      }))

      if (exportFormat === 'csv') {
        const headers = Object.keys(rows[0])
        const csvContent = [
          headers.join(','),
          ...rows.map(row => headers.map(h => `"${String(row[h] || '').replace(/"/g, '""')}"`).join(','))
        ].join('\n')

        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `wb_operations_${exportStartDate}_${exportEndDate}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        const worksheet = XLSX.utils.json_to_sheet(rows)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, "Операции")
        worksheet["!cols"] = Object.keys(rows[0]).map(() => ({ wch: 20 }))

        XLSX.writeFile(workbook, `wb_operations_${exportStartDate}_${exportEndDate}.xlsx`)
      }
      
      setIsExportModalOpen(false)
      toast.success('Выгрузка готова!')
    } catch (err) {
      console.error(err)
      toast.error('Ошибка выгрузки')
    } finally {
      setExporting(false)
    }
  }

  const loadImportHistory = useCallback(async () => {
    try {
      const data = await api.getImportHistory('work_logs')
      setImportHistory(data)
    } catch (err) {
      console.error(err)
    }
  }, [])

  const handleRollbackImport = async (importId) => {
    try {
      setDeletingBatch(importId)
      await api.deleteImportBatch('work_logs', importId)
      toast.success('Импорт успешно отменен')
      await loadRecords()
      await loadImportHistory()
    } catch (err) {
      console.error(err)
      toast.error('Ошибка при отмене импорта')
    } finally {
      setDeletingBatch(null)
    }
  }

  useEffect(() => {
    if (isAdmin && showImportHistory) {
      loadImportHistory()
    }
  }, [isAdmin, showImportHistory, loadImportHistory])

  // Helper для получения инфы о типе
  const getTypeInfo = useCallback((typeStr) => {
    if (!typeStr) return { title: 'Неизвестно', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>, color: 'var(--text-secondary)', isCustom: false }
    if (typeStr === 'expense_var') return { title: 'Расходник', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></svg>, color: 'var(--danger)', isCustom: false }
    if (typeStr.startsWith('custom:')) {
      const id = typeStr.split(':')[1]
      const rule = customRules.find(r => r.id === id)
      return { title: rule ? rule.name : 'Кастомная работа', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>, color: 'var(--warning)', isCustom: true, rule }
    }
    const base = baseActions.find(a => a.id === typeStr)
    if (base) return { ...base, isCustom: false }
    return { title: 'Неизвестно', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>, color: 'var(--text-secondary)', isCustom: false }
  }, [customRules, baseActions])

  // Сводка за выбранный период
  const displayedRecords = useMemo(() => {
    let result = records
    if (filterEmployee) {
      result = result.filter(r => r.employee_id === filterEmployee)
    }
    if (filterType !== 'all') {
      result = result.filter(r => r.type === filterType)
    }
    if (filterSearch) {
      const q = filterSearch.toLowerCase()
      result = result.filter(r => {
        const info = getTypeInfo(r.type)
        const title = info ? info.title.toLowerCase() : ''
        const box = (r.box_number || '').toLowerCase()
        const comment = (r.comment || '').toLowerCase()
        const emp = (r.employee_name || '').toLowerCase()
        return title.includes(q) || box.includes(q) || comment.includes(q) || emp.includes(q)
      })
    }
    return result
  }, [records, filterEmployee, filterType, filterSearch, getTypeInfo])

  const summary = useMemo(() => {
    const s = { acceptance: 0, issued: 0, returns: 0, custom: 0, expense_var: 0 }
    displayedRecords.forEach(r => {
      if (r.type === 'acceptance') s.acceptance += (r.count || 0) + (r.count_late || 0)
      else if (r.type === 'issued') s.issued += (r.count || 0)
      else if (r.type === 'returns') s.returns += (r.count || 0)
      else if (r.type === 'expense_var') s.expense_var += (r.count || 0)
      else if (r.type && r.type.startsWith('custom:')) s.custom += (r.count || 0)
    })
    return s
  }, [displayedRecords])

  return (
    <div className="operations-page animate-fade-in">
      
      <div className="operations-header">
        <div>
          <h1 className="operations-title">{displayAsAdmin ? 'Операции' : 'Рабочая смена'}</h1>
          <p className="operations-subtitle">{displayAsAdmin ? 'Управление операциями' : 'Рабочий стол сотрудника'}</p>
        </div>
        {displayAsAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              className={`btn ${showShiftTimerForAdmin ? 'btn-success' : 'btn-secondary'} btn-sm`}
              onClick={handleToggleAdminShiftMode}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 600,
                flexShrink: 0
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
              {showShiftTimerForAdmin ? 'Скрыть смену' : 'Выйти на смену'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowImportHistory(true)} style={{ padding: '6px 10px', fontSize: '11px', whiteSpace: 'nowrap', flexShrink: 0 }}>
              История
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowImport(true)} style={{ padding: '6px 10px', fontSize: '11px', whiteSpace: 'nowrap', flexShrink: 0 }}>
              Импорт
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setIsExportModalOpen(true)} style={{ padding: '6px 10px', fontSize: '11px', whiteSpace: 'nowrap', flexShrink: 0 }}>
              Экспорт
            </button>
          </div>
        )}
      </div>

      {/* Переключатель вкладок */}
      <div className="operations-tabs" style={{ marginBottom: '20px' }}>
        <button 
          type="button"
          className={`operations-tab ${activeTab === 'operations' ? 'active' : ''}`}
          onClick={() => setActiveTab('operations')}
        >
          Все операции
        </button>
        <button 
          type="button"
          className={`operations-tab ${activeTab === 'supplies' ? 'active' : ''}`}
          onClick={() => setActiveTab('supplies')}
          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          Заявки на хознужды
          {unreadSuppliesCount > 0 && (
            <span style={{ 
              background: 'var(--danger)', 
              color: 'white', 
              fontSize: '11px', 
              fontWeight: 700,
              padding: '2px 6px', 
              borderRadius: '10px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1
            }}>
              {unreadSuppliesCount}
            </span>
          )}
        </button>
        {canSeeHistory && (
          <button 
            type="button"
            className={`operations-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            История внесений
          </button>
        )}
      </div>

      {activeTab === 'operations' && (
        <>
          {/* Таймер смены (для линейных сотрудников ВСЕГДА, либо для Админа/Владельца при выходе на смену) */}
          {(!isAdmin || showShiftTimerForAdmin || isDemoStaffView) && (
            <div style={{ marginBottom: '20px' }} data-tour="shift-timer">
              <ShiftTimer profile={profile} salaryConfig={salaryConfig} isDemo={isDemoStaffView} />
            </div>
          )}

          {/* Переключатель режимов: Лента / Шахматка */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div className="chess-view-switcher" data-tour="operations-view-switcher">
              <button
                type="button"
                className={`chess-view-btn ${viewMode === 'feed' ? 'active' : ''}`}
                onClick={() => handleSetViewMode('feed')}
              >
                Лента
              </button>
              <button
                type="button"
                className={`chess-view-btn ${viewMode === 'chess' ? 'active' : ''}`}
                onClick={() => handleSetViewMode('chess')}
              >
                Шахматка
              </button>
            </div>
          </div>

          {viewMode === 'chess' ? (
            <div data-tour="operations-chess-grid" className="animate-fade-in" style={{ width: '100%' }}>
              <ChessGrid
                employees={employees}
                filterEmployee={filterEmployee}
                isAdmin={isAdmin}
                userPermissions={userPermissions}
                profile={profile}
                customRules={customRules}
                salaryConfig={salaryConfig}
                activePvz={activePvz}
                onRecordsUpdated={() => { loadRecords(); refreshMarkers() }}
                canAddForOthers={canAddForOthers}
                onAddRecord={handleChessCellClick}
                onEditRecord={handleEdit}
                onQuickAdd={handleQuickAdd}
                shiftMarkers={shiftMarkers}
                onDismissShift={handleDismissShift}
                onRestoreShift={handleRestoreShift}
                selectedPvzIds={selectedPvzIds}
                userPvzs={userPvzs}
                getPvzColor={getPvzColor}
                getPvzFullName={getPvzFullName}
                getPvzShortName={getPvzShortName}
              />
            </div>
          ) : (
            <OperationsFeed
              displayedRecords={displayedRecords}
              loading={loading}
              isAdmin={isAdmin}
              userPermissions={userPermissions}
              profile={profile}
              activePvz={activePvz}
              employees={employees}
              records={records}
              salaryConfig={salaryConfig}
              customRules={customRules}
              filterStartDate={filterStartDate}
              setFilterStartDate={setFilterStartDate}
              filterEndDate={filterEndDate}
              setFilterEndDate={setFilterEndDate}
              filterEmployee={filterEmployee}
              setFilterEmployee={setFilterEmployee}
              filterType={filterType}
              setFilterType={setFilterType}
              filterSearch={filterSearch}
              setFilterSearch={setFilterSearch}
              summary={summary}
              baseActions={baseActions}
              getTypeInfo={getTypeInfo}
              shiftMarkers={shiftMarkers}
              handleQuickAdd={handleQuickAdd}
              setShowAddMenu={setShowAddMenu}
              setQuickAddContext={setQuickAddContext}
              handleEdit={handleEdit}
              itemToDelete={itemToDelete}
              setItemToDelete={setItemToDelete}
              confirmDelete={confirmDelete}
              todayIso={todayIso}
              yesterdayIso={yesterdayIso}
              monthStartIso={monthStartIso}
              monthEndIso={monthEndIso}
              quarterStartIso={quarterStartIso}
              quarterEndIso={quarterEndIso}
              selectedPvzIds={selectedPvzIds}
              userPvzs={userPvzs}
              getPvzColor={getPvzColor}
              getPvzShortName={getPvzShortName}
              getPvzFullName={getPvzFullName}
              pendingChangeRequests={pendingChangeRequests}
              handleResolveChangeRequest={handleResolveChangeRequest}
            />
          )}
        </>
      )}

      {activeTab === 'supplies' && (
        <SupplyRequests
          profile={profile}
          isAdmin={isAdmin}
          isOwner={isOwner}
          userPermissions={userPermissions}
          activePvz={activePvz}
          employees={employees}
          loadRecords={loadRecords}
          selectedPvzIds={selectedPvzIds}
          userPvzs={userPvzs}
          getPvzColor={getPvzColor}
          getPvzShortName={getPvzShortName}
          getPvzFullName={getPvzFullName}
        />
      )}

      {activeTab === 'history' && canSeeHistory && (
        <div className="operations-history-tab">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>История изменений операций</h3>
            <button className="btn btn-secondary btn-sm" onClick={loadHistory} disabled={loadingHistory}>
              {loadingHistory ? 'Обновление...' : 'Обновить'}
            </button>
          </div>

          {loadingHistory && historyRecords.length === 0 ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Загрузка истории...
            </div>
          ) : historyRecords.length === 0 ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-secondary)', border: '1px dashed var(--border-color)', borderRadius: '12px' }}>
              Нет записей в истории
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {historyRecords.map(log => {
                const dateFormatted = new Date(log.created_at).toLocaleString('ru-RU', {
                  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                });

                let actionText = '';
                let details = '';
                const data = log.action === 'delete' ? log.old_data : log.new_data;
                const oldData = log.old_data;

                const typeLabels = { acceptance: 'Приёмка', issued: 'Выдача', returns: 'Возврат', expense_var: 'Хозтовары' };
                const opType = data?.type || '';
                const opLabel = typeLabels[opType] || (opType.startsWith('custom:') ? 'Доп. правило' : opType);

                const empObj = employees.find(e => e.id === data?.employee_id);
                const forEmployeeName = empObj ? (empObj.full_name || empObj.name) : (data?.employee_name || 'Неизвестный');

                if (log.action === 'create') {
                  actionText = 'добавил(а)';
                  if (log.target_table === 'expenses') {
                    details = `Расходник на сумму ${data?.amount || 0} ₽ (Категория: ${data?.category || 'Хозтовары'}, Комментарий: "${data?.comment || ''}")`;
                  } else {
                    details = `Операцию "${opLabel}" (${data?.count || 0} шт) для сотрудника ${forEmployeeName} на дату ${data?.work_date}`;
                  }
                } else if (log.action === 'delete') {
                  actionText = 'удалил(а)';
                  if (log.target_table === 'expenses') {
                    details = `Расходник на сумму ${oldData?.amount || 0} ₽ (Категория: ${oldData?.category || 'Хозтовары'}, Комментарий: "${oldData?.comment || ''}")`;
                  } else {
                    details = `Операцию "${opLabel}" (${oldData?.count || 0} шт) сотрудника ${forEmployeeName} за дату ${oldData?.work_date}`;
                  }
                } else if (log.action === 'update') {
                  actionText = 'изменил(а)';
                  if (log.target_table === 'expenses') {
                    details = `Расходник с ${oldData?.amount || 0} ₽ на ${data?.amount || 0} ₽ (Комментарий: "${data?.comment || ''}")`;
                  } else {
                    const countDiff = oldData?.count !== data?.count ? `кол-во с ${oldData?.count || 0} на ${data?.count || 0}` : '';
                    const commentDiff = oldData?.comment !== data?.comment ? `комментарий: "${data?.comment || ''}"` : '';
                    const diffText = [countDiff, commentDiff].filter(Boolean).join(', ');
                    details = `Операцию "${opLabel}" сотрудника ${forEmployeeName} за дату ${data?.work_date} (${diffText || 'изменены параметры'})`;
                  }
                }

                const actionColor = log.action === 'create' ? 'var(--success)' : log.action === 'delete' ? 'var(--danger)' : 'var(--warning)';

                return (
                  <div 
                    key={log.id} 
                    style={{ 
                      background: 'var(--surface-color)', 
                      border: '1px solid var(--border-color)', 
                      borderRadius: '12px', 
                      padding: '12px 16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
                        {log.actor_name || 'Система'} <span style={{ color: actionColor, fontWeight: 700 }}>{actionText}</span>
                      </span>
                      <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{dateFormatted}</span>
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                      {details}
                    </div>
                    {log.ip_address && (
                      <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', alignSelf: 'flex-end', marginTop: '2px' }}>
                        IP: {log.ip_address}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Модалки для работы с операциями */}
      <OperationFormModal
        showModal={showModal}
        setShowModal={setShowModal}
        showAddMenu={showAddMenu}
        setShowAddMenu={setShowAddMenu}
        activeActionType={activeActionType}
        editingId={editingId}
        formData={formData}
        setFormData={setFormData}
        confirmData={confirmData}
        setConfirmData={setConfirmData}
        saving={saving}
        quickAddContext={quickAddContext}
        setQuickAddContext={setQuickAddContext}
        quickAddSelectedTypes={quickAddSelectedTypes}
        setQuickAddSelectedTypes={setQuickAddSelectedTypes}
        shiftMarkers={shiftMarkers}
        employees={employees}
        customRules={customRules}
        canAddForOthers={canAddForOthers}
        profile={profile}
        isAdmin={isAdmin}
        userPermissions={userPermissions}
        getTypeInfo={getTypeInfo}
        baseActions={baseActions}
        handleOpenAction={handleOpenAction}
        handleDismissShift={handleDismissShift}
        handleQuickAdd={handleQuickAdd}
        handlePreSubmit={handlePreSubmit}
        handleSubmit={handleSubmit}
        today={today}
        selectedPvzIds={selectedPvzIds}
        userPvzs={userPvzs}
      />

      {/* Модалки экспорта и импорта */}
      <ExportImportModals
        isAdmin={isAdmin}
        isExportModalOpen={isExportModalOpen}
        setIsExportModalOpen={setIsExportModalOpen}
        exportFormat={exportFormat}
        setExportFormat={setExportFormat}
        exportStartDate={exportStartDate}
        setExportStartDate={setExportStartDate}
        exportEndDate={exportEndDate}
        setExportEndDate={setExportEndDate}
        exportColumns={exportColumns}
        setExportColumns={setExportColumns}
        exporting={exporting}
        handleExport={handleExport}
        showImport={showImport}
        setShowImport={setShowImport}
        employees={employees}
        loadRecords={loadRecords}
        showImportHistory={showImportHistory}
        setShowImportHistory={setShowImportHistory}
        importHistory={importHistory}
        deletingBatch={deletingBatch}
        handleRollbackImport={handleRollbackImport}
      />
    </div>
  )
}
